/**
 * PdfPagePickerModal — when a PDF turns out to have more than one page where the app expects
 * exactly one (a QC round's drawing, a Drawing Sheet revision), this lets the user pick the page
 * they meant and extracts ONLY that page before anything is uploaded. Thumbnails render via
 * pdf.js and the single-page extraction runs via pdf-lib — both entirely client-side, so the
 * pages the user doesn't pick never leave the browser tab, let alone touch a server or a Storage
 * bucket. See docs/QC_WORKFLOW.md and docs/DRAWING_SHEETS upload notes.
 *
 * Usage: const { resolveUpload, picker } = usePdfPagePicker()
 *        const resolved = await resolveUpload(file)   // null if the user cancelled
 *        if (!resolved) return
 *        // resolved is the original file (single-page PDFs pass through untouched) or the
 *        // extracted single-page File (multi-page PDFs, after the user picks one)
 *        // ...continue the existing upload with `resolved` in place of the raw file...
 *        return <>{picker}{...rest of your JSX}</>
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { loadPdfjs } from '../lib/pdfjs.js'

const IS_PDF = file => /\.pdf$/i.test(file.name || '') || file.type === 'application/pdf'

export function usePdfPagePicker() {
  const [pending, setPending] = useState(null) // { file, resolve } | null

  const resolveUpload = useCallback(file => {
    if (!file || !IS_PDF(file)) return Promise.resolve(file)
    return (async () => {
      const pdfjs = await loadPdfjs()
      if (!pdfjs) return file // pdf.js failed to load — don't block the upload over a preview failure
      let numPages = 1
      try {
        const buf = await file.arrayBuffer()
        const doc = await pdfjs.getDocument({ data: buf }).promise
        numPages = doc.numPages
      } catch {
        return file // unreadable here — let the normal upload path surface the real error
      }
      if (numPages <= 1) return file
      return new Promise(resolve => setPending({ file, resolve }))
    })()
  }, [])

  const picker = pending ? (
    <PdfPagePickerModal
      file={pending.file}
      onConfirm={extracted => { pending.resolve(extracted); setPending(null) }}
      onCancel={() => { pending.resolve(null); setPending(null) }}
    />
  ) : null

  return { resolveUpload, picker }
}

export default function PdfPagePickerModal({ file, onConfirm, onCancel }) {
  const [phase, setPhase] = useState('loading') // loading | pick | extracting | error
  const [pages, setPages] = useState([])
  const [selected, setSelected] = useState(null)
  const [error, setError] = useState('')
  const cancelledRef = useRef(false)

  useEffect(() => {
    cancelledRef.current = false
    ;(async () => {
      try {
        const pdfjs = await loadPdfjs()
        if (!pdfjs) throw new Error('PDF preview could not load. Check your connection and try again.')
        const buf = await file.arrayBuffer()
        const pdfDoc = await pdfjs.getDocument({ data: buf }).promise
        const list = []
        for (let n = 1; n <= pdfDoc.numPages; n++) {
          if (cancelledRef.current) return
          const page = await pdfDoc.getPage(n)
          const vp1 = page.getViewport({ scale: 1 })
          const scale = 160 / vp1.width
          const vp = page.getViewport({ scale })
          const canvas = document.createElement('canvas')
          canvas.width = Math.ceil(vp.width); canvas.height = Math.ceil(vp.height)
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise
          list.push({ page: n, thumb: canvas.toDataURL('image/jpeg', 0.75) })
          if (!cancelledRef.current) setPages([...list])
        }
        if (!cancelledRef.current) setPhase('pick')
      } catch (err) {
        if (!cancelledRef.current) { setError(err.message || 'The file could not be read.'); setPhase('error') }
      }
    })()
    return () => { cancelledRef.current = true }
  }, [file])

  async function handleUse() {
    if (selected == null) return
    setPhase('extracting'); setError('')
    try {
      const { PDFDocument } = await import('pdf-lib')
      const buf = await file.arrayBuffer()
      const src = await PDFDocument.load(buf)
      const out = await PDFDocument.create()
      const [copied] = await out.copyPages(src, [selected - 1])
      out.addPage(copied)
      const bytes = await out.save()
      const base = (file.name || 'drawing').replace(/\.pdf$/i, '')
      const extracted = new File([bytes], base + ' p' + selected + '.pdf', { type: 'application/pdf' })
      onConfirm(extracted)
    } catch (err) {
      setError(err.message || 'The selected page could not be extracted.')
      setPhase('pick')
    }
  }

  return (
    <div style={S.overlay} onClick={phase === 'extracting' ? undefined : onCancel}>
      <div style={S.card} onClick={e => e.stopPropagation()}>
        <div style={S.title}>Multiple pages found</div>
        <div style={S.hint}>
          {phase === 'loading' ? 'Reading ' + file.name + '…'
            : phase === 'extracting' ? 'Extracting the selected page…'
            : phase === 'error' ? error
            : '"' + file.name + '" has ' + pages.length + ' pages. Pick the one you want — the rest won’t be uploaded.'}
        </div>
        {(phase === 'loading' || phase === 'pick') && (
          <div style={S.grid}>
            {pages.map(pg => (
              <button key={pg.page} onClick={() => setSelected(pg.page)}
                style={{ ...S.thumbBtn, borderColor: selected === pg.page ? 'var(--aj-accent)' : 'var(--aj-border-mid)' }}>
                <img src={pg.thumb} alt={'Page ' + pg.page} style={S.thumbImg} />
                <span style={S.pageLbl}>p{pg.page}</span>
              </button>
            ))}
            {phase === 'loading' && pages.length === 0 && <div style={S.muted}>Rendering pages…</div>}
          </div>
        )}
        {phase === 'error' && (
          <div style={S.footer}>
            <button style={S.btnGhost} onClick={onCancel}>Close</button>
          </div>
        )}
        {(phase === 'loading' || phase === 'pick' || phase === 'extracting') && (
          <div style={S.footer}>
            <button style={S.btnGhost} onClick={onCancel} disabled={phase === 'extracting'}>Cancel</button>
            <button style={{ ...S.btnPrimary, opacity: selected != null && phase === 'pick' ? 1 : 0.5 }}
              onClick={handleUse} disabled={selected == null || phase !== 'pick'}>
              {phase === 'extracting' ? 'Extracting…' : 'Use this page'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const S = {
  overlay: { position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  card: { width: 560, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-surface)', borderRadius: 10, border: '0.5px solid var(--aj-border-mid)', boxShadow: '0 12px 40px rgba(0,0,0,0.35)', padding: 18 },
  title: { fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--aj-text)' },
  hint: { marginTop: 6, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.5, color: 'var(--aj-text-mid)' },
  grid: { marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 10, overflowY: 'auto', paddingRight: 2 },
  thumbBtn: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: 4, background: 'var(--aj-bg-elevated)', border: '2px solid var(--aj-border-mid)', borderRadius: 6, cursor: 'pointer' },
  thumbImg: { width: '100%', height: 'auto', display: 'block', border: '0.5px solid var(--aj-border)', borderRadius: 2 },
  pageLbl: { fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)' },
  muted: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', gridColumn: '1 / -1' },
  footer: { marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 },
  btnGhost: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'transparent', color: 'var(--aj-text-mid)', cursor: 'pointer' },
  btnPrimary: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 4, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer' },
}
